import { apiSlice } from "./apiSlice";

export interface SalesforceIssue {
    id: string;
    study: string;
    instrument: string;
    username: string;
    description: string;
    variableLabel: string;
}

export type SalesforceIssueResponse = {
    totalSize: number;
    done: boolean;
    nextRecordsUrl: string;
    records: SalesforceIssueRecord[];
}

export type SalesforceIssueRecord = {
    "id": string,
    "name": string,
    "status": string,
    "approved": string,
    "datasetVersion": string,
    "instrument": string,
    "issueType": string,
    "datasetName": string
}

export const issueApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getSalesforceIssues: builder.query<SalesforceIssue[], { instrument: string; domain: string }>({
            query: ({ instrument, domain }) => `/salesforce/issues?instrument=${instrument}&domain=${domain}`,
        }),
        getAllIssues: builder.query({
            query: () => '/issue_tracker/?format=json',
            providesTags: [{ type: 'Issues', id: 'LIST' }],
            transformResponse: (responseData: any) => {
                const mapped: any[] = []

                responseData.data.forEach((issueData: any[]) => {
                    const issue = {
                        id: issueData[0],
                        title: issueData[1],
                        module: issueData[2],
                        category: issueData[3],
                        reporter: issueData[4],
                        assignee: issueData[5],
                        status: issueData[6],
                        priority: issueData[7],
                        site: issueData[8],
                        pscid: issueData[9],
                        visitLabel: issueData[10],
                        dateCreated: issueData[11],
                        lastUpdated: issueData[12],
                        sessionID: issueData[13],
                        candID: issueData[14],
                        watching: issueData[15],
                    }
                    mapped.push(issue);
                });
                return mapped;
            }
        }),
        getIssue: builder.query({
            query: ({ id }) => `issue_tracker/Edit/?issueID=${id}`,
            providesTags: (result) => {
                return [{ type: 'Issues', id: result.issueData.issueID }]
            }
        }),
        updateIssue: builder.mutation({
            query: (updatedFields) => {
                return {
                    url: '/issue_tracker/Edit/',
                    method: 'POST',
                    body: updatedFields
                }
            },
            invalidatesTags: (result => [{ type: 'Issues', id: result.issueID }, { type: 'Issues', id: 'LIST' }])
        }),
        newIssue: builder.mutation({
            query: (updatedFields) => {
                return {
                    url: '/issue_tracker/Edit/',
                    method: 'POST',
                    body: updatedFields
                }
            },
            invalidatesTags: (() => [{ type: 'Issues', id: 'LIST' }])
        }),
        addComment: builder.mutation({
            query: (updatedFields) => {
                return {
                    url: '/issue_tracker/Edit/',
                    method: 'PUT',
                    body: updatedFields
                }
            },
            invalidatesTags: (result => [{ type: 'Issues', id: result.issueID }])
        }),
        getSalesforceRelease: builder.query<SalesforceIssueResponse, {studyName: string, version: string}>({
            query: ({ studyName, version }) => `/salesforce/release-approvals?datasetVersion=${version}&studyName=${studyName}`,
        }),
    })
});

export const {
    useGetSalesforceIssuesQuery,
    useGetAllIssuesQuery,
    useGetIssueQuery,
    useUpdateIssueMutation,
    useNewIssueMutation,
    useAddCommentMutation,
    useGetSalesforceReleaseQuery,
} = issueApiSlice;
